import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Center, useFetch, SelectBox } from "@vertesia/ui/core";
import { LastSelectedAccountId_KEY, LastSelectedProjectId_KEY, useUserSession } from "@vertesia/ui/session";
import { LockIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useUITranslation } from '../../i18n/index.js';
import { AppInstallationProvider } from "./AppInstallationProvider";
export function StandaloneApp({ name, AccessDenied = AccessDeniedMessage, children }) {
    return name ? (_jsx(StandaloneAppImpl, { name: name, AccessDenied: AccessDenied, children: children })) : (_jsx(UnknownAppName, {}));
}
export function StandaloneAppImpl({ name, AccessDenied = AccessDeniedMessage, children }) {
    const { authToken, client } = useUserSession();
    const [installation, setInstallation] = useState(null);
    const [state, setState] = useState("loading");
    useEffect(() => {
        if (!authToken) {
            setState("loading");
        }
        else {
            const isAppVisible = authToken.apps.includes(name);
            if (isAppVisible) {
                client.apps.getAppInstallationByName(name).then(inst => {
                    if (!inst) {
                        console.log(`App ${name} not found!`);
                        setState("error");
                    }
                    else {
                        setState("loaded");
                        setInstallation(inst);
                    }
                });
            }
            else {
                setState("error");
            }
        }
    }, [name, authToken]);
    if (state === "loading") {
        return null;
    }
    else if (state === "error") {
        return _jsx(AccessDenied, { name: name });
    }
    else if (installation) {
        return _jsx(AppInstallationProvider, { installation: installation, children: children });
    }
}
function AccessDeniedMessage({ name }) {
    const { project, accounts, client } = useUserSession();
    const { t } = useUITranslation();
    const [selectedAccountId, setSelectedAccountId] = useState();
    // Fetch all projects where the app is installed across all organizations
    const { data: allProjects } = useFetch(() => {
        return client.apps.getAppInstallationProjects({ name });
    }, [name]);
    // Group projects by organization
    const { projectsByOrg, orgOptions } = useMemo(() => {
        if (!allProjects || !accounts)
            return { projectsByOrg: {}, orgOptions: [] };
        const grouped = {};
        for (const p of allProjects) {
            if (!grouped[p.account]) {
                grouped[p.account] = [];
            }
            grouped[p.account].push(p);
        }
        // Only show orgs that have projects with the app installed
        const orgsWithProjects = accounts.filter(a => grouped[a.id]?.length > 0);
        return { projectsByOrg: grouped, orgOptions: orgsWithProjects };
    }, [allProjects, accounts]);
    // Auto-select first org if not selected
    useEffect(() => {
        if (!selectedAccountId && orgOptions.length > 0) {
            setSelectedAccountId(orgOptions[0].id);
        }
    }, [orgOptions, selectedAccountId]);
    const onProjectChange = (selected) => {
        localStorage.setItem(LastSelectedAccountId_KEY, selected.account);
        localStorage.setItem(LastSelectedProjectId_KEY + '-' + selected.account, selected.id);
        window.location.reload();
    };
    const filteredProjects = selectedAccountId ? (projectsByOrg[selectedAccountId] || []) : [];
    const selectedOrg = orgOptions.find(a => a.id === selectedAccountId);
    return (_jsxs(Center, { className: "pt-10 flex flex-col items-center text-center text-gray-700", children: [_jsx(LockIcon, { className: "w-10 h-10 mb-4 text-gray-500" }), _jsx("div", { className: "text-xl font-semibold", children: t('shell.accessDenied') }), _jsxs("div", { className: "mt-2 text-sm text-gray-500", children: ["You don't have permission to view the ", _jsx("span", { className: "font-semibold", children: name }), " app in project: ", _jsxs("span", { className: "font-semibold", children: ["\u00AB", project?.name, "\u00BB"] }), "."] }), orgOptions.length === 0 && allProjects !== undefined && (_jsx("div", { className: "mt-4 text-sm text-gray-500", children: "This app is not installed in any project you have access to." })), orgOptions.length > 0 && (_jsxs("div", { className: "mt-4 flex flex-row gap-4 items-end", children: [orgOptions.length > 1 && (_jsxs("div", { children: [_jsx("div", { className: "text-sm text-gray-500 mb-2", children: t('shell.organization') }), _jsx(SelectBox, { by: "id", value: selectedOrg, options: orgOptions, optionLabel: (option) => option.name, placeholder: t('shell.selectOrganization'), onChange: (org) => setSelectedAccountId(org.id) })] })), _jsxs("div", { children: [orgOptions.length > 1 && _jsx("div", { className: "text-sm text-gray-500 mb-2", children: t('login.terminal.project') }), _jsx(SelectBox, { by: "id", value: undefined, options: filteredProjects, optionLabel: (option) => option.name, placeholder: t('shell.selectProject'), onChange: onProjectChange })] })] }))] }));
}
function UnknownAppName() {
    const { t } = useUITranslation();
    return (_jsxs(Center, { className: "pt-10 flex flex-col items-center text-center text-gray-700", children: [_jsx(LockIcon, { className: "w-10 h-10 mb-4 text-gray-500" }), _jsx("div", { className: "text-xl font-semibold", children: t('shell.applicationNotRegistered') }), _jsxs("div", { className: "mt-2 text-sm text-gray-500", children: ["Before starting to code a Vertesia application you must register an application manifest in Vertesia Studio then install it in one or more projects.", _jsx("p", {}), "Then use the created app name as a parameter to ", _jsx("code", { children: "<StandaloneApp name=\"your-app-name\">" }), " in the ", _jsx("code", { children: "src/main.tsx" }), " file."] })] }));
}
//# sourceMappingURL=StandaloneApp.js.map